import React, { useMemo, useState } from "react";
import { ExportStatus } from "@iventis/domain-model/model/exportStatus";
import { Body2, Body3, InteractiveElement, StyledInteractiveArea, borderRadius, fontSizes, styled } from "@iventis/styles";
import { ProgressSpinnerComponent } from "@iventis/components";
import { timeAgo } from "@iventis/utilities";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getExportNotificationText } from "./export-plan-notification-helpers";
import { NotificationStatusComponent } from "./notification-status";
import { UserNotificationBaseProps, MapExportNotification } from "./notification-types";

interface ExportPlanNotificationProps extends UserNotificationBaseProps {
    notification: MapExportNotification;
    onDownloadClicked: (notificationId: string, assetId: string, name: string) => void;
}

/** Notification for a plan export */
export const ExportPlanNotificationComponent = ({ notification, onDownloadClicked, onClick }: ExportPlanNotificationProps) => {
    const translate = useIventisTranslate();
    const { lastUpdatedAt, resourceData, readAt } = notification;
    const { exportFormat, exportName, exportStatus: status, assetId } = resourceData;
    const [read, setRead] = useState(readAt != null);
    const exportInProgress = status !== ExportStatus.Completed && status !== ExportStatus.Failed;
    // Status is the only value which can change the display text
    const displayText = useMemo(() => getExportNotificationText(exportFormat, status, exportName, translate), [status]);

    const handleClick = () => {
        setRead(true);
        onClick();
    };

    return (
        <ExportPlanNotificationContainer inProgress={exportInProgress} read={read} data-testid={`export-plan-notification-${notification.id}`}>
            {/* Click anywhere on the component to mark as read */}
            {!read && !exportInProgress && (
                <StyledInteractiveArea zIndex={100} onClick={handleClick}>
                    <span className="hidden">{translate(Content.notifications.markAsRead)}</span>
                </StyledInteractiveArea>
            )}
            <div className="profile-image-container">{exportInProgress ? <ProgressSpinnerComponent height="30px" /> : <FontAwesomeIcon size="lg" icon={["far", "upload"]} />}</div>
            <div className="export-notification-body">
                <div data-testid="notification-title-section" className="notification-section">
                    <div className="name-date no-text-overflow">
                        <Body2 className="notification-title no-text-overflow">{`${exportFormat.toLocaleUpperCase()} ${translate(Content.notifications.planExport.export)}`}</Body2>
                    </div>
                    <NotificationStatusComponent status={status} notificationId={notification.id} />
                </div>
                <div className="notification-section">
                    <Body3 data-testid="notification-body" data-notificationid={notification.id}>
                        {displayText}
                    </Body3>
                </div>
                <div className="notification-footer">
                    <Body3 className="created-at no-text-overflow">{timeAgo(lastUpdatedAt, translate(Content.comments.justNow), true)}</Body3>

                    <StyledInteractiveElement
                        disabled={status !== ExportStatus.Completed}
                        data-testid="notification-view-export"
                        className="view-export-button"
                        onClick={() => onDownloadClicked(notification.id, assetId, exportName)}
                    >
                        {translate(Content.notifications.planExport.download)}
                    </StyledInteractiveElement>
                </div>
            </div>
        </ExportPlanNotificationContainer>
    );
};

export const ExportPlanNotificationContainer = styled.div<{ read: boolean; inProgress: boolean }>`
    width: 270px;
    box-sizing: border-box;
    position: relative;

    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;

    background-color: ${({ read, inProgress, theme }) => (inProgress ? theme.secondaryColors.warm : read ? theme.secondaryColors.blank : theme.tertiaryColors.primaryBackground)};
    border-radius: ${borderRadius.standard};
    padding: 10px;

    .profile-image-container {
        width: 44px;
        height: 44px;

        display: flex;
        justify-content: center;
        align-items: center;
        flex-shrink: 0;
        border-radius: ${borderRadius.circle};
        background-color: ${({ theme, inProgress }) => (inProgress ? theme.secondaryColors.blank : theme.primaryColors.subdued70)};
        color: ${({ theme }) => theme.secondaryColors.blank};

        &--skeleton {
            background-color: ${({ theme }) => theme.secondaryColors.blank};
        }
    }

    .export-notification-body {
        display: flex;
        // Profile image is 44px pixels wide
        width: calc(100% - 44px);
        flex-direction: column;
        gap: 5px;
    }

    .notification-section {
        display: flex;
        gap: 5px;
        align-items: center;
    }

    .name-date {
        display: flex;
        flex-grow: 1;
        gap: 10px;
    }

    .notification-title {
        font-weight: 500;
    }

    .created-at {
        color: ${({ theme }) => theme.typographyColors.subdued};
    }

    // Ensures that the name or date does not overflow
    .no-text-overflow {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    .user-profile-image {
        height: 36px;
        width: 36px;
    }

    .view-export-button {
        font-weight: 500;
        text-decoration: underline;
        font-size: ${fontSizes.xSmall};
        z-index: 200;
        :not([disabled]) {
            color: ${({ theme }) => theme.primaryColors.focus};
        }
    }

    .notification-footer {
        display: flex;
        justify-content: space-between;
    }
`;

const StyledInteractiveElement = styled(InteractiveElement)`
    :disabled {
        cursor: not-allowed;
    }
`;
